import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, Text } from '../ui';

const LOADERS_DATA = {
  brand_dna: {
    rotate_every_seconds: 7,
    messages: [
      { message: '🌐 Opening your website...', fact: '💡 Most branding agencies take 2–4 weeks to do what we\'re doing right now.' },
      { message: '📖 Reading through your pages...', fact: '🏆 Brands with a clear identity are 3x more recognizable to their customers.' },
      { message: '🔍 Scanning your content and messaging...', fact: '🧠 It takes 5–7 brand impressions before a customer remembers you.' },
      { message: '🎨 Getting a feel for your visual style...', fact: '🖌️ Visual consistency increases brand recognition by 80%.' },
      { message: '🗣️ Picking up on how you talk to your customers...', fact: '🤝 Consistent brand voice increases revenue by up to 23%.' },
      { message: '❤️ Sensing the emotions your brand evokes...', fact: '😊 77% of customers buy based on brand personality, not just price.' },
      { message: '🧩 Piecing together your brand personality...', fact: '📖 Storytelling makes your brand 22x more memorable than facts alone.' },
      { message: '🎯 Identifying who your audience really is...', fact: '💡 Brands that know their audience convert 2x more visitors into customers.' },
      { message: '🔑 Uncovering what makes you different...', fact: '🏅 Businesses with a clear USP close deals 30% faster.' },
      { message: '🌟 Highlighting your strongest qualities...', fact: '💰 Strong brand differentiation lets you charge up to 20% more for your services.' },
      { message: '📡 Understanding where you sit in your market...', fact: '📈 Well-positioned brands grow revenue 2x faster than their competitors.' },
      { message: '🖋️ Capturing your brand\'s story...', fact: '❤️ Emotionally connected customers have a 306% higher lifetime value.' },
      { message: '🧬 Mapping out your core values...', fact: '📊 Companies with strong brand values outperform competitors by 202%.' },
      { message: '💬 Defining your tone of voice...', fact: '🗣️ Clear communication reduces customer confusion and increases trust by 60%.' },
      { message: '🧠 Connecting all the dots...', fact: '🎉 You just saved yourself weeks of back-and-forth with a branding consultant.' },
      { message: '✨ Finalizing your Brand DNA...', fact: '💰 A branding agency would charge $2,000–$5,000 for this. You\'re getting it in seconds.' },
    ],
  },
  social_media_posts: {
    rotate_every_seconds: 8,
    messages: [
      { message: '📋 Loading your Brand DNA...', fact: '📈 Businesses that post consistently get 67% more leads than those that don\'t.' },
      { message: '🔍 Getting to know your business...', fact: '🗣️ Brands with a consistent voice are 4x more likely to be trusted.' },
      { message: '🎯 Figuring out who your audience is...', fact: '❤️ People are 3x more likely to engage with content that feels personal.' },
      { message: '🤔 Thinking about what problems your audience faces...', fact: '🙌 Content that solves a problem gets shared 3x more than promotional content.' },
      { message: '💡 Brainstorming ideas your audience will love...', fact: '🧠 Brands that educate their audience see 131% more traffic.' },
      { message: '🌍 Thinking about the best time to post...', fact: '📅 Posts published at peak times get 3x more reach than off-peak ones.' },
      { message: '🎨 Matching the vibe of your brand...', fact: '🖌️ Visual consistency across posts increases brand recognition by 80%.' },
      { message: '🎙️ Warming up your brand voice...', fact: '🚀 Posts that match your brand voice get 2x more engagement than generic content.' },
      { message: '✍️ Writing your first post...', fact: '👁️ You have just 1.7 seconds to grab someone\'s attention on social media.' },
      { message: '📸 Crafting captions that stop the scroll...', fact: '🎣 A strong opening line increases post read-through rate by 60%.' },
      { message: '📣 Writing posts that spark conversations...', fact: '💬 Posts that ask questions get 100% more comments than those that don\'t.' },
      { message: '🔥 Making sure every post earns its place...', fact: '📊 Only 20% of social posts drive 80% of all engagement. We\'re aiming for that 20%.' },
      { message: '📱 Optimizing posts for mobile readers...', fact: '📲 Over 80% of social media is consumed on mobile devices.' },
      { message: '🌱 Building content that grows your audience...', fact: '📈 Brands that post 3–5 times per week grow their following 4x faster.' },
      { message: '🔗 Connecting your story to your audience\'s needs...', fact: '⭐ 88% of consumers trust a business more after seeing consistent social activity.' },
      { message: '📝 Drafting posts that convert followers into customers...', fact: '💰 Social media drives 78% of small business sales when used consistently.' },
      { message: '🏷️ Adding the right hooks to each post...', fact: '⏰ Small business owners spend 5+ hours a week on social media. That time is yours back.' },
      { message: '⭐ Making your business shine online...', fact: '💰 A social media manager charges $300–$600/month just to write posts. You\'re doing it in under a minute.' },
      { message: '🪄 Giving every post a final polish...', fact: '🤝 Consistent social media presence increases brand trust by up to 74%.' },
      { message: '🏁 Wrapping everything up...', fact: '🎉 You just created a week\'s worth of content in under 60 seconds.' },
    ],
  },
};

const LoaderWithFacts = ({ type = 'brand_dna', title, subtitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const loader = LOADERS_DATA[type];
  const current = loader.messages[currentIndex];

  useEffect(() => {
    const interval = setInterval(() => {
      // Fade out
      setVisible(false);

      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % loader.messages.length);
        setVisible(true);
      }, 400);
    }, loader.rotate_every_seconds * 1000);

    return () => clearInterval(interval);
  }, [loader]);

  return (
    <div className='w-full h-full flex items-center justify-center p-2 sm:p-4'>
      <div className='flex items-center justify-center self-center px-4 w-full max-w-md'>
        <Card className='w-full shadow-none' style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <Card.Body className='flex flex-col items-center justify-center text-center space-y-4 py-8'>

            <Loader2 className='w-14 h-14 animate-spin' style={{ color: 'var(--brand)' }} />

            {title && (
              <Text variant='heading' className='text-xl md:text-2xl'>
                {title}
              </Text>
            )}

            <div
              className='flex flex-col items-center space-y-3'
              style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}
            >
              {/* Message */}
              <Text variant='subtitle' className='text-base font-medium' style={{ color: 'var(--text-body)' }}>
                {current.message}
              </Text>

              {/* Fact pill */}
              <div className='rounded-xl px-4 py-3 max-w-sm' style={{ background: 'var(--bg-card-alt)', border: '1px solid var(--border)' }}>
                <Text className='text-sm' style={{ color: 'var(--text-muted)' }}>
                  {current.fact}
                </Text>
              </div>
            </div>

          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default LoaderWithFacts;